import type { GameType, HighlightConfig, SimpleDate } from './types';

/*
  What a game *is*, as opposed to how a particular client draws it.

  Each game package owns its own entry (see `@puzzlio/<game>/config`) and @puzzlio/games collects
  them. Everything here has to mean something on any client: no Tailwind class names, no React
  components, no `var(--x)` — those live in the web's GameConfig, which spreads this and adds them.

  The rule of thumb for what belongs: if a second client would need it to show the game correctly,
  it goes here.
*/

/**
 * How available a game is to the player in front of us:
 *  - `available`      — anyone can play it.
 *  - `accountHolders` — listed and linked like an available game, but its puzzle pages ask a player
 *                       without an account to create one before they can play.
 *  - `earlyAccess`    — playable, but by subscribers only. Anything that sends a player to a game has
 *                       to check their subscription first, or it lands them on a paywall.
 *  - `comingSoon`     — registered (admin tooling, DB ids) but not playable by anyone yet.
 */
export type GameAvailability = 'available' | 'accountHolders' | 'earlyAccess' | 'comingSoon';

/**
 * A difficulty tier of standalone practice puzzles. `level` gives the difficulty (1 = Easy,
 * 2 = Medium, 3 = Hard); `puzzles` are board slugs, played at `${url}/puzzle/${slug}`.
 */
export interface PracticeCollection {
  level: number;
  puzzles: string[];
}

/** The brand colour a game identifies with, and which its primary buttons take. */
export type GameButtonColor = 'yellow' | 'green' | 'blue' | 'purple';

/**
 * The game's page background: a solid fill with a halftone of dots over it.
 *
 * Resolved colour values rather than token names — both clients can paint a colour, but only the web
 * could resolve a `var()`. The games look them up in `@puzzlio/tokens` where the colour is a palette
 * entry; a few of these backgrounds are off-palette and stay literals, each saying so.
 */
export interface GameBackground {
  fill: string;
  dot: string;
}

/**
 * The two colours the solved screen's burst fades between: `inner` at its middle, `outer` through
 * the rays. Resolved values for the same reason the background's are; every one of them is a token.
 */
export interface GameBurst {
  inner: string;
  outer: string;
}

/**
 * The largest a board's cell is drawn, in px on the web and pt in the app. A board takes the room it's
 * given only up to this: past it, a small grid on a big screen is just a few huge squares.
 */
export const MAX_BOARD_CELL = 64;

export interface GameMeta {
  type: GameType;
  /** Database id, needed by anything that calls the API. */
  gameId: number;
  label: string;
  /** One line describing how the game is played, as its card shows it on any client. */
  description: string;
  emoji: string;
  buttonColor: GameButtonColor;
  background: GameBackground;
  burst: GameBurst;
  /** The rules that set off a section of the game's page, such as the ask under a solved puzzle. A resolved token. */
  separator: string;
  /** Oldest board that exists, bounding the archive. */
  earliestDate: SimpleDate;
  /** Slug of the board that ends the tutorial; completing it means the player finished it. */
  tutorialSlug: string;
  practiceCollections: PracticeCollection[];
  /** Whether the game offers a Hint button. */
  enableHints: boolean;
  availability: GameAvailability;
  /** Static highlighter palette (mode key → colour/label). Absent for games without a highlighter. */
  highlightConfigs?: Record<string, HighlightConfig>;
  /*
    Route paths. Shared rather than redeclared per client so a deep link and a web URL can't drift
    apart — the app's routes mirror these exactly, which is the whole reason it uses file-based
    routing.
  */
  url: string;
  gameURL: (date: SimpleDate) => string;
  /** A day's puzzle with its comments open, at the first unread reply to the player. */
  commentsURL: (date: SimpleDate) => string;
  /** The archive, opened to the month containing `fromDate` (the latest month if omitted). */
  archiveURL: (fromDate?: SimpleDate) => string;
  /** The weekly challenge for the week starting Monday `week`. */
  challengeURL: (week: SimpleDate) => string;
  tutorialURL: string;
  /** The "learn more about this game" page, linked from How to Play. */
  introURL: string;
}

/** The query parameter on a puzzle's URL that opens its comments; see `commentsURL`. */
export const COMMENTS_PARAM = 'comments';

/** Builds the standard route helpers from a game's base path, which all four games share. */
export function gameRoutes(
  url: string
): Pick<GameMeta, 'url' | 'gameURL' | 'commentsURL' | 'archiveURL' | 'challengeURL' | 'tutorialURL' | 'introURL'> {
  return {
    url,
    gameURL: (date: SimpleDate) => `${url}/${date}`,
    commentsURL: (date: SimpleDate) => `${url}/${date}?${COMMENTS_PARAM}`,
    archiveURL: (fromDate?: SimpleDate) => `${url}/archives${fromDate ? `?day=${fromDate}` : ''}`,
    challengeURL: (week: SimpleDate) => `${url}/challenge/${week}`,
    tutorialURL: `${url}/tutorial`,
    introURL: `${url}/intro`,
  };
}
